import React, { useState, useMemo } from "react";
import { Card, Col, Row, Select, Spin, Statistic, DatePicker } from "antd";
import Flex from "components/shared-components/Flex";
import DataDisplayWidget from "components/shared-components/DataDisplayWidget";
import ChartWidget from "components/shared-components/ChartWidget";
import ApexChart from "react-apexcharts";
import { COLORS } from "constants/ChartConstant";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import locale from "antd/es/date-picker/locale/nl_NL";

import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  EuroOutlined,
  FileDoneOutlined,
  ShoppingOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useSalesAggregates } from "../../../../data/hooks";

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

const { Option } = Select;
const { RangePicker } = DatePicker;

const PERIOD_OPTIONS = {
  THIS_WEEK: "this_week",
  LAST_WEEK: "last_week",
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_QUARTER: "this_quarter",
  LAST_QUARTER: "last_quarter",
  THIS_YEAR: "this_year",
  LAST_YEAR: "last_year",
  CUSTOM: "custom",
};

const COMPARISON_OPTIONS = {
  PREVIOUS_PERIOD: "previous_period",
  PREVIOUS_YEAR: "previous_year",
  CUSTOM: "custom",
};

const getPeriodDates = (period, customStart = null, customEnd = null) => {
  const now = dayjs();

  switch (period) {
    case PERIOD_OPTIONS.THIS_WEEK:
      return {
        start: now.startOf("isoWeek").unix(),
        end: now.endOf("day").unix(),
        label: `Week ${now.isoWeek()} ${now.year()}`,
        duration: now.diff(now.startOf("isoWeek"), "day") + 1,
      };
    case PERIOD_OPTIONS.LAST_WEEK:
      return {
        start: now.subtract(1, "week").startOf("isoWeek").unix(),
        end: now.subtract(1, "week").endOf("isoWeek").unix(),
        label: `Week ${now.subtract(1, "week").isoWeek()} ${now.subtract(1, "week").year()}`,
        duration: 7,
      };
    case PERIOD_OPTIONS.THIS_MONTH:
      return {
        start: now.startOf("month").unix(),
        end: now.endOf("day").unix(),
        label: now.format("MMMM YYYY"),
        duration: now.diff(now.startOf("month"), "day") + 1,
      };
    case PERIOD_OPTIONS.LAST_MONTH:
      return {
        start: now.subtract(1, "month").startOf("month").unix(),
        end: now.subtract(1, "month").endOf("month").unix(),
        label: now.subtract(1, "month").format("MMMM YYYY"),
        duration: now.subtract(1, "month").daysInMonth(),
      };
    case PERIOD_OPTIONS.THIS_QUARTER:
      return {
        start: now.startOf("quarter").unix(),
        end: now.endOf("day").unix(),
        label: `Q${now.quarter()} ${now.year()}`,
        duration: now.diff(now.startOf("quarter"), "day") + 1,
      };
    case PERIOD_OPTIONS.LAST_QUARTER:
      return {
        start: now.subtract(1, "quarter").startOf("quarter").unix(),
        end: now.subtract(1, "quarter").endOf("quarter").unix(),
        label: `Q${now.subtract(1, "quarter").quarter()} ${now.subtract(1, "quarter").year()}`,
        duration:
          now.subtract(1, "quarter").endOf("quarter").diff(now.subtract(1, "quarter").startOf("quarter"), "day") + 1,
      };
    case PERIOD_OPTIONS.THIS_YEAR:
      return {
        start: now.startOf("year").unix(),
        end: now.endOf("day").unix(),
        label: now.year().toString(),
        duration: now.diff(now.startOf("year"), "day") + 1,
      };
    case PERIOD_OPTIONS.LAST_YEAR:
      return {
        start: now.subtract(1, "year").startOf("year").unix(),
        end: now.subtract(1, "year").endOf("year").unix(),
        label: now.subtract(1, "year").year().toString(),
        duration: 365,
      };
    case PERIOD_OPTIONS.CUSTOM:
      if (customStart && customEnd) {
        const start = dayjs(customStart);
        const end = dayjs(customEnd);
        return {
          start: start.startOf("day").unix(),
          end: end.endOf("day").unix(),
          label: `${start.format("DD MMM")} - ${end.format("DD MMM YYYY")}`,
          duration: end.diff(start, "day") + 1,
        };
      }
      return {
        start: now.startOf("month").unix(),
        end: now.endOf("day").unix(),
        label: now.format("MMMM YYYY"),
        duration: now.diff(now.startOf("month"), "day") + 1,
      };
    default:
      return {
        start: now.startOf("month").unix(),
        end: now.endOf("day").unix(),
        label: now.format("MMMM YYYY"),
        duration: now.diff(now.startOf("month"), "day") + 1,
      };
  }
};

const getComparisonDates = (
  currentPeriod,
  comparisonType,
  currentStart,
  currentEnd,
  customStart = null,
  customEnd = null,
) => {
  const start = dayjs.unix(currentStart);
  const end = dayjs.unix(currentEnd);
  const duration = end.diff(start, "day");

  switch (comparisonType) {
    case COMPARISON_OPTIONS.PREVIOUS_PERIOD:
      return {
        start: start.subtract(duration + 1, "day").unix(),
        end: start.subtract(1, "day").unix(),
        label: `${start.subtract(duration + 1, "day").format("DD MMM")} - ${start.subtract(1, "day").format("DD MMM YYYY")}`,
      };
    case COMPARISON_OPTIONS.PREVIOUS_YEAR:
      return {
        start: start.subtract(1, "year").unix(),
        end: end.subtract(1, "year").unix(),
        label: `${start.subtract(1, "year").format("DD MMM")} - ${end.subtract(1, "year").format("DD MMM YYYY")}`,
      };
    case COMPARISON_OPTIONS.CUSTOM:
      if (customStart && customEnd) {
        const compStart = dayjs(customStart);
        const compEnd = dayjs(customEnd);
        return {
          start: compStart.startOf("day").unix(),
          end: compEnd.endOf("day").unix(),
          label: `${compStart.format("DD MMM")} - ${compEnd.format("DD MMM YYYY")}`,
        };
      }
      return {
        start: start.subtract(duration + 1, "day").unix(),
        end: start.subtract(1, "day").unix(),
        label: `${start.subtract(duration + 1, "day").format("DD MMM")} - ${start.subtract(1, "day").format("DD MMM YYYY")}`,
      };
    default:
      return {
        start: start.subtract(1, "year").unix(),
        end: end.subtract(1, "year").unix(),
        label: `${start.subtract(1, "year").format("DD MMM")} - ${end.subtract(1, "year").format("DD MMM YYYY")}`,
      };
  }
};

const RevenueOverTimeChart = ({ currentData, comparisonData, currentLabel, comparisonLabel }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    if (!currentData?.getDailyRevenueData || !comparisonData?.getDailyRevenueData) {
      return { series: [], categories: [] };
    }

    const current = currentData.getDailyRevenueData();
    const comparison = comparisonData.getDailyRevenueData();

    if (!current?.data || !comparison?.data || current.data.length === 0) {
      return { series: [], categories: [] };
    }

    return {
      series: [
        { name: currentLabel, data: current.data },
        { name: comparisonLabel, data: comparison.data },
      ],
      categories: current.labels,
    };
  }, [currentData, comparisonData, currentLabel, comparisonLabel]);

  if (chartData.series.length === 0) {
    return (
      <Card title={t("revenue_over_time")}>
        <div style={{ padding: "50px", textAlign: "center", color: "#999" }}>{t("no_data_available")}</div>
      </Card>
    );
  }

  return (
    <Card title={t("revenue_over_time")}>
      <ApexChart
        options={{
          colors: [COLORS[0], COLORS[3]],
          stroke: { curve: "smooth", width: 3 },
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          xaxis: {
            categories: chartData.categories,
            labels: { rotate: -45, rotateAlways: true },
          },
          yaxis: {
            labels: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
          tooltip: {
            y: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
        }}
        series={chartData.series}
        type="line"
        height={350}
      />
    </Card>
  );
};

const LandelijkVsRegionaalChart = ({ data, label }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const landelijkVsRegionaal = data.getLandelijkVsRegionaalData();

    return {
      series: [
        { name: t("vce_landelijk"), data: landelijkVsRegionaal.vceLandelijk },
        { name: t("hvo_landelijk"), data: landelijkVsRegionaal.hvoLandelijk },
        { name: t("vce_regionaal"), data: landelijkVsRegionaal.vceRegionaal },
      ],
      categories: landelijkVsRegionaal.labels,
    };
  }, [data, t]);

  return (
    <Card title={`${t("landelijk_vs_regionaal")} - ${label}`}>
      <ApexChart
        options={{
          colors: [COLORS[1], COLORS[4], COLORS[2]],
          chart: { stacked: true, toolbar: { show: false }, zoom: { enabled: false } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: chartData.categories,
            labels: { rotate: -45, rotateAlways: true },
          },
          yaxis: {
            labels: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
          tooltip: {
            y: {
              formatter: (value, { seriesIndex, dataPointIndex, w }) => {
                const total = w.globals.stackedSeriesTotals[dataPointIndex];
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${CURRENCY}${formatPrice(value)} (${percentage}%)`;
              },
            },
          },
        }}
        series={chartData.series}
        type="bar"
        height={350}
      />
    </Card>
  );
};

const AverageOrderValueChart = ({ currentData, comparisonData, currentLabel, comparisonLabel }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    if (!currentData?.getDailyRevenueData || !currentData?.getDailyOrdersData) {
      return { series: [], categories: [] };
    }

    const currentRevenue = currentData.getDailyRevenueData();
    const currentOrders = currentData.getDailyOrdersData();
    const comparisonRevenue = comparisonData.getDailyRevenueData();
    const comparisonOrders = comparisonData.getDailyOrdersData();

    if (!currentRevenue.data || !currentOrders.data || currentRevenue.data.length === 0) {
      return { series: [], categories: [] };
    }

    const currentAOV = currentRevenue.data.map((rev, idx) => {
      const orders = currentOrders.data[idx];
      return orders > 0 ? parseFloat((rev / orders).toFixed(2)) : 0;
    });

    const comparisonAOV = comparisonRevenue.data.map((rev, idx) => {
      const orders = comparisonOrders.data[idx];
      return orders > 0 ? parseFloat((rev / orders).toFixed(2)) : 0;
    });

    return {
      series: [
        { name: currentLabel, data: currentAOV },
        { name: comparisonLabel, data: comparisonAOV },
      ],
      categories: currentRevenue.labels,
    };
  }, [currentData, comparisonData, currentLabel, comparisonLabel]);

  if (chartData.series.length === 0) {
    return (
      <Card title={t("average_order_value")}>
        <div style={{ padding: "50px", textAlign: "center", color: "#999" }}>{t("no_data_available")}</div>
      </Card>
    );
  }

  return (
    <Card title={t("average_order_value")}>
      <ApexChart
        options={{
          colors: [COLORS[4], COLORS[5]],
          stroke: { curve: "smooth", width: 3 },
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          xaxis: {
            categories: chartData.categories,
            labels: { rotate: -45, rotateAlways: true },
          },
          yaxis: {
            labels: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
          tooltip: {
            y: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
        }}
        series={chartData.series}
        type="line"
        height={350}
      />
    </Card>
  );
};

const NewVsReturningChart = ({ data, label }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const newVsReturning = data.getNewVsReturningData();

    return {
      series: [
        { name: t("returning_customers"), data: newVsReturning.returningCustomers },
        { name: t("new_customers"), data: newVsReturning.newCustomers },
      ],
      categories: newVsReturning.labels,
    };
  }, [data, t]);

  return (
    <Card title={`${t("new_vs_returning_customers")} - ${label}`}>
      <ApexChart
        options={{
          colors: ["#ff6b6b", COLORS[6]],
          chart: { stacked: false, toolbar: { show: false }, zoom: { enabled: false } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: chartData.categories,
            labels: { rotate: -45, rotateAlways: true },
          },
          yaxis: {
            labels: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
          tooltip: {
            y: { formatter: (value) => CURRENCY + formatPrice(value) },
          },
          plotOptions: {
            bar: { columnWidth: "60%" },
          },
        }}
        series={chartData.series}
        type="bar"
        height={350}
      />
    </Card>
  );
};

const NewVsReturningDonutChart = ({ data, label }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const newRevenue = parseFloat(data?.newRevenue) || 0;
    const returningRevenue = parseFloat(data?.returningRevenue) || 0;
    const total = newRevenue + returningRevenue;

    if (total === 0 || !newRevenue || !returningRevenue) {
      return { series: [], labels: [], isEmpty: true };
    }

    const newPercentage = ((newRevenue / total) * 100).toFixed(1);
    const returningPercentage = ((returningRevenue / total) * 100).toFixed(1);

    return {
      series: [Math.round(newRevenue), Math.round(returningRevenue)],
      labels: [`${t("new_customers")} (${newPercentage}%)`, `${t("returning_customers")} (${returningPercentage}%)`],
      isEmpty: false,
    };
  }, [data, t]);

  if (chartData.isEmpty) {
    return (
      <Card title={`${t("revenue_distribution")} - ${label}`}>
        <div style={{ padding: "50px", textAlign: "center", color: "#999" }}>{t("no_data_available")}</div>
      </Card>
    );
  }

  return (
    <Card title={`${t("revenue_distribution")} - ${label}`}>
      <ApexChart
        options={{
          colors: [COLORS[6], "#ff6b6b"],
          labels: chartData.labels,
          legend: { position: "bottom" },
          chart: { toolbar: { show: false } },
          tooltip: {
            y: {
              formatter: (value, { seriesIndex, w }) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return CURRENCY + formatPrice(value) + " (" + pct + "%)";
              },
            },
          },
          dataLabels: {
            formatter: (val) => val.toFixed(1) + "%",
          },
        }}
        series={chartData.series}
        type="donut"
        height={350}
      />
    </Card>
  );
};

const OrdersComparisonChart = ({ currentData, comparisonData, currentLabel, comparisonLabel }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const currentOrders = currentData?.totalOrders || 0;
    const comparisonOrders = comparisonData?.totalOrders || 0;

    return {
      series: [
        { name: currentLabel, data: [currentOrders] },
        { name: comparisonLabel, data: [comparisonOrders] },
      ],
      categories: [t("total_orders")],
    };
  }, [currentData, comparisonData, currentLabel, comparisonLabel, t]);

  return (
    <Card title={t("number_of_orders")}>
      <ApexChart
        options={{
          colors: [COLORS[0], COLORS[3]],
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: chartData.categories,
          },
          plotOptions: {
            bar: { horizontal: false, columnWidth: "55%" },
          },
          yaxis: {
            labels: { formatter: (value) => Math.round(value) },
          },
          tooltip: {
            y: { formatter: (value) => Math.round(value) + " bestellingen" },
          },
        }}
        series={chartData.series}
        type="bar"
        height={300}
      />
    </Card>
  );
};

const CustomerRetentionWidget = ({ currentData, comparisonData, currentLabel, comparisonLabel }) => {
  const { t } = useTranslation();

  const currentRetention = useMemo(() => {
    const total = (currentData.newCustomers || 0) + (currentData.returningCustomers || 0);
    if (total === 0) return 0;
    return ((currentData.returningCustomers / total) * 100).toFixed(1);
  }, [currentData]);

  const comparisonRetention = useMemo(() => {
    const total = (comparisonData.newCustomers || 0) + (comparisonData.returningCustomers || 0);
    if (total === 0) return 0;
    return ((comparisonData.returningCustomers / total) * 100).toFixed(1);
  }, [comparisonData]);

  const retentionChange = useMemo(() => {
    if (comparisonRetention === 0) return 0;
    return (currentRetention - comparisonRetention).toFixed(1);
  }, [currentRetention, comparisonRetention]);

  return (
    <Card title={t("customer_retention_rate")} style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Statistic
            title={currentLabel}
            value={currentRetention}
            suffix="%"
            valueStyle={{ color: "#1890ff", fontSize: "24px" }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={comparisonLabel}
            value={comparisonRetention}
            suffix="%"
            valueStyle={{ color: "#8c8c8c", fontSize: "24px" }}
          />
        </Col>
      </Row>
      <div style={{ marginTop: 16, padding: "12px", backgroundColor: "#f0f2f5", borderRadius: "4px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: "16px", fontWeight: "bold", marginRight: 8 }}>Verschil:</span>
          <span
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: parseFloat(retentionChange) >= 0 ? "#3f8600" : "#cf1322",
            }}
          >
            {parseFloat(retentionChange) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {` ${Math.abs(retentionChange)}%`}
          </span>
        </div>
        <div style={{ fontSize: "12px", color: "#595959", marginBottom: 8 }}>
          <strong>{t("retention_explanation")}</strong>
        </div>
        <div style={{ fontSize: "11px", color: "#8c8c8c", fontStyle: "italic" }}>
          {t("retention_calculation")}
        </div>
      </div>
    </Card>
  );
};

const CustomerRetentionChart = ({ currentData, comparisonData, currentLabel, comparisonLabel }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    if (!currentData?.salesAggregates || !comparisonData?.salesAggregates) {
      return { series: [], categories: [] };
    }

    const currentAggregates = currentData.salesAggregates;
    const comparisonAggregates = comparisonData.salesAggregates;

    if (currentAggregates.length === 0) {
      return { series: [], categories: [] };
    }

    const currentRetentionData = currentAggregates.map((item) => {
      const total = (item.newCustomers?.orderCount || 0) + (item.returningCustomers?.orderCount || 0);
      if (total === 0) return 0;
      return parseFloat(((item.returningCustomers?.orderCount || 0) / total * 100).toFixed(1));
    });

    const comparisonRetentionData = comparisonAggregates.map((item) => {
      const total = (item.newCustomers?.orderCount || 0) + (item.returningCustomers?.orderCount || 0);
      if (total === 0) return 0;
      return parseFloat(((item.returningCustomers?.orderCount || 0) / total * 100).toFixed(1));
    });

    return {
      series: [
        { name: currentLabel, data: currentRetentionData },
        { name: comparisonLabel, data: comparisonRetentionData },
      ],
      categories: currentAggregates.map((item) => item.date),
    };
  }, [currentData, comparisonData, currentLabel, comparisonLabel]);

  if (chartData.series.length === 0) {
    return (
      <Card title={t("retention_trend")} style={{ marginTop: 16 }}>
        <div style={{ padding: "50px", textAlign: "center", color: "#999" }}>{t("no_data_available")}</div>
      </Card>
    );
  }

  return (
    <Card title={t("retention_trend")} style={{ marginTop: 16 }}>
      <ApexChart
        options={{
          colors: [COLORS[0], COLORS[3]],
          stroke: { curve: "smooth", width: 3 },
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          xaxis: {
            categories: chartData.categories,
            labels: { rotate: -45, rotateAlways: true },
          },
          yaxis: {
            labels: { formatter: (value) => value.toFixed(1) + "%" },
            min: 0,
            max: 100,
          },
          tooltip: {
            y: { formatter: (value) => value.toFixed(1) + "%" },
          },
        }}
        series={chartData.series}
        type="line"
        height={300}
      />
    </Card>
  );
};

const SalesDashboard = () => {
  const { t } = useTranslation();
  const [currentPeriod, setCurrentPeriod] = useState(PERIOD_OPTIONS.THIS_MONTH);
  const [comparisonType, setComparisonType] = useState(COMPARISON_OPTIONS.PREVIOUS_YEAR);
  const [customCurrentRange, setCustomCurrentRange] = useState(null);
  const [customComparisonRange, setCustomComparisonRange] = useState(null);

  const currentDates = useMemo(
    () => getPeriodDates(currentPeriod, customCurrentRange?.[0], customCurrentRange?.[1]),
    [currentPeriod, customCurrentRange],
  );

  const comparisonDates = useMemo(
    () =>
      getComparisonDates(
        currentPeriod,
        comparisonType,
        currentDates.start,
        currentDates.end,
        customComparisonRange?.[0],
        customComparisonRange?.[1],
      ),
    [currentPeriod, comparisonType, currentDates.start, currentDates.end, customComparisonRange],
  );

  const currentData = useSalesAggregates({
    startDate: currentDates.start,
    endDate: currentDates.end,
  });

  const comparisonData = useSalesAggregates({
    startDate: comparisonDates.start,
    endDate: comparisonDates.end,
  });

  const percentageChange = useMemo(() => {
    if (!comparisonData.totalRevenue || comparisonData.totalRevenue === 0) return 0;
    return (((currentData.totalRevenue - comparisonData.totalRevenue) / comparisonData.totalRevenue) * 100).toFixed(1);
  }, [currentData.totalRevenue, comparisonData.totalRevenue]);

  if (currentData.isLoading || comparisonData.isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  return (
    <DashboardContainer>
      <Row gutter={16} className="mb-3">
        <Col xs={24} sm={24} md={24} lg={24}>
          <PageTitle>{t("sales_report")}</PageTitle>
          <div style={{ marginTop: "8px", fontSize: "14px", color: "#666" }}>
            <strong>{t("current_period")}:</strong> {dayjs.unix(currentDates.start).format("DD-MM-YYYY")} {t("to")}{" "}
            {dayjs.unix(currentDates.end).format("DD-MM-YYYY")}
            {" | "}
            <strong>{t("comparison_period")}:</strong> {dayjs.unix(comparisonDates.start).format("DD-MM-YYYY")}{" "}
            {t("to")} {dayjs.unix(comparisonDates.end).format("DD-MM-YYYY")}
          </div>
        </Col>
      </Row>
      <Row gutter={16} className="mb-3">
        <Col xs={24} sm={24} md={12} lg={12}></Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Flex gap="8px" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Select
              value={currentPeriod}
              onChange={(value) => {
                setCurrentPeriod(value);
                if (value !== PERIOD_OPTIONS.CUSTOM) {
                  setCustomCurrentRange(null);
                }
              }}
              style={{ minWidth: 150 }}
              placeholder={t("select_period")}
            >
              <Option value={PERIOD_OPTIONS.THIS_WEEK}>{t("this_week")}</Option>
              <Option value={PERIOD_OPTIONS.LAST_WEEK}>{t("last_week")}</Option>
              <Option value={PERIOD_OPTIONS.THIS_MONTH}>{t("this_month")}</Option>
              <Option value={PERIOD_OPTIONS.LAST_MONTH}>{t("last_month")}</Option>
              <Option value={PERIOD_OPTIONS.THIS_QUARTER}>{t("this_quarter")}</Option>
              <Option value={PERIOD_OPTIONS.LAST_QUARTER}>{t("last_quarter")}</Option>
              <Option value={PERIOD_OPTIONS.THIS_YEAR}>{t("this_year")}</Option>
              <Option value={PERIOD_OPTIONS.LAST_YEAR}>{t("last_year")}</Option>
              <Option value={PERIOD_OPTIONS.CUSTOM}>{t("custom_period")}</Option>
            </Select>
            {currentPeriod === PERIOD_OPTIONS.CUSTOM && (
              <RangePicker
                value={customCurrentRange}
                onChange={setCustomCurrentRange}
                format="DD-MM-YYYY"
                locale={locale}
                style={{ minWidth: 250 }}
              />
            )}
            <span style={{ alignSelf: "center", marginInline: "8px" }}>{t("vs")}</span>
            <Select
              value={comparisonType}
              onChange={(value) => {
                setComparisonType(value);
                if (value !== COMPARISON_OPTIONS.CUSTOM) {
                  setCustomComparisonRange(null);
                }
              }}
              style={{ minWidth: 180 }}
              placeholder={t("compare_with")}
            >
              <Option value={COMPARISON_OPTIONS.PREVIOUS_YEAR}>{t("previous_year_same_period")}</Option>
              <Option value={COMPARISON_OPTIONS.PREVIOUS_PERIOD}>{t("previous_period")}</Option>
              <Option value={COMPARISON_OPTIONS.CUSTOM}>{t("custom_period")}</Option>
            </Select>
            {comparisonType === COMPARISON_OPTIONS.CUSTOM && (
              <RangePicker
                value={customComparisonRange}
                onChange={setCustomComparisonRange}
                format="DD-MM-YYYY"
                locale={locale}
                style={{ minWidth: 250 }}
              />
            )}
          </Flex>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card>
            <Statistic
              title={`${t("revenue")} - ${currentDates.label}`}
              value={currentData.totalRevenue}
              precision={2}
              prefix={CURRENCY}
              valueStyle={{ color: parseFloat(percentageChange) >= 0 ? "#3f8600" : "#cf1322" }}
              suffix={
                <span style={{ fontSize: "14px" }}>
                  {parseFloat(percentageChange) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {` ${Math.abs(percentageChange)}%`}
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<FileDoneOutlined />}
            value={currentData.totalOrders}
            title={t("total_orders")}
            color="cyan"
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<BarChartOutlined />}
            value={CURRENCY + formatPrice(currentData.averageOrderValue)}
            title={t("average_order_value")}
            color="blue"
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<UserAddOutlined />}
            value={currentData.newCustomers}
            title={t("new_customers")}
            color="green"
            vertical={true}
            avatarSize={55}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<ShoppingOutlined />}
            value={currentData.vceLandelijkOrders}
            title={t("vce_landelijk_orders")}
            color="purple"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[1] + "15"}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<EuroOutlined />}
            value={CURRENCY + formatPrice(currentData.vceLandelijkRevenue)}
            title={t("vce_landelijk_revenue")}
            color="gold"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[1] + "15"}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<ShoppingOutlined />}
            value={currentData.hvoLandelijkOrders}
            title={t("hvo_landelijk_orders")}
            color="geekblue"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[4] + "15"}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<EuroOutlined />}
            value={CURRENCY + formatPrice(currentData.hvoLandelijkRevenue)}
            title={t("hvo_landelijk_revenue")}
            color="cyan"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[4] + "15"}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<ShoppingOutlined />}
            value={currentData.vceRegionaalOrders}
            title={t("vce_regionaal_orders")}
            color="orange"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[2] + "15"}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DataDisplayWidget
            icon={<EuroOutlined />}
            value={CURRENCY + formatPrice(currentData.vceRegionaalRevenue)}
            title={t("vce_regionaal_revenue")}
            color="volcano"
            vertical={true}
            avatarSize={55}
            backgroundColor={COLORS[2] + "15"}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <RevenueOverTimeChart
            currentData={currentData}
            comparisonData={comparisonData}
            currentLabel={currentDates.label}
            comparisonLabel={comparisonDates.label}
          />
        </Col>
        <Col xs={24} lg={8}>
          <OrdersComparisonChart
            currentData={currentData}
            comparisonData={comparisonData}
            currentLabel={currentDates.label}
            comparisonLabel={comparisonDates.label}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <LandelijkVsRegionaalChart data={currentData} label={currentDates.label} />
        </Col>
        <Col xs={24} lg={12}>
          <AverageOrderValueChart
            currentData={currentData}
            comparisonData={comparisonData}
            currentLabel={currentDates.label}
            comparisonLabel={comparisonDates.label}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <NewVsReturningChart data={currentData} label={currentDates.label} />
          <CustomerRetentionChart
            currentData={currentData}
            comparisonData={comparisonData}
            currentLabel={currentDates.label}
            comparisonLabel={comparisonDates.label}
          />
        </Col>
        <Col xs={24} lg={8}>
          <NewVsReturningDonutChart data={currentData} label={currentDates.label} />
          <CustomerRetentionWidget
            currentData={currentData}
            comparisonData={comparisonData}
            currentLabel={currentDates.label}
            comparisonLabel={comparisonDates.label}
          />
        </Col>
      </Row>
    </DashboardContainer>
  );
};

const DashboardContainer = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin-bottom: 0;
`;

export default SalesDashboard;
